/**
 * useLoadingAssets — host loading 遮罩的 **per-game 背景圖**（約定即註冊，不碰 content.ts）。
 *
 * 檔案放：
 *
 *   src/assets/loading/<gameId>/zh.{webp,png,jpg,jpeg}
 *   src/assets/loading/<gameId>/en.{webp,png,jpg,jpeg}
 *
 * **丟檔案即生效**（`import.meta.glob` eager 掃、build-time 變成 hash URL），沒圖的 gameId →
 * `undefined` → `LoadingScreen` 走通用版（黑底 + logo）。
 *
 * 🛑 **放圖不等於啟用新版 loading** —— 要不要走新版面只看 Cocos 的 `hostLoading` 旗標
 * （`App.vue` 的 `showNewLoadingUi`）。所以可以放心把所有款的圖都放進來當素材庫，
 * 舊款照樣走 logo，畫面不受影響。
 *
 * 🛑 為什麼**不是**雲端圖（imgByTag）、也**不是** `src/assets/<gameId>/`：
 *   - 遮罩從 app mount 就要顯示，雲端圖要「登入 → 查 tag → 取圖」三步才拿得到，太晚
 *     （協議 docs/LOADING_PROTOCOL.md〈五〉早已定案「打包在 host 專案內」）。
 *   - `src/assets/<gameId>/` 是雲端圖片管線的暫存區，Phase 4 上雲後**整個資料夾會被刪**
 *     （cjbqb / fcyxx 都刪過）。loading 圖放那裡會跟著消失。所以獨立一個 `loading/` 頂層資料夾。
 *
 * 🛑 不要用 `resolveImg()` 包這裡的 URL —— 它不是 `imgtag:`，是 Vite 靜態資源 URL，直接用。
 *    （resolveImg 對非 imgtag 字串原樣放行，包了也不會壞，只是沒意義。）
 *
 * 單張 webp 建議壓到 ~200KB 內（中英各一，整款 ~400KB）：玩家只會抓當下這款那一張
 * （glob 用 `?url`，bundle 裡只有 URL 字串），但它是遮罩上唯一的大圖，太大會拖慢
 * 「玩家看到第一個畫面」的時間 —— 那正是做這個遮罩的目的。
 *
 * ⚠️ 這些圖**進版控**（二進位、沒有 delta 壓縮），85 款 ×2 張約 41MB：`.git` 與每次
 * CI checkout / 部署上傳都會等量增加。補圖前先壓，進了 git 歷史就拿不掉。
 */

/** gameId → locale → URL（eager：build-time 就是 URL 字串，沒有 lazy import） */
const LOADING_BG = import.meta.glob<string>('../assets/loading/*/*.{webp,png,jpg,jpeg}', {
  eager: true,
  query: '?url',
  import: 'default',
})

type Locale = 'zh' | 'en'

/** `../assets/loading/<gameId>/<locale>.<ext>` → `{ gameId, locale }` */
const parseKey = (path: string): { gameId: string; locale: Locale } | null => {
  const m = /\/loading\/([^/]+)\/(zh|en)\.(?:webp|png|jpe?g)$/.exec(path)
  return m ? { gameId: m[1]!, locale: m[2] as Locale } : null
}

/** 整理成 `{ [gameId]: { zh?, en? } }`（module init 一次） */
const BY_GAME: Record<string, Partial<Record<Locale, string>>> = {}
for (const [path, url] of Object.entries(LOADING_BG)) {
  const k = parseKey(path)
  if (!k) continue
  ;(BY_GAME[k.gameId] ??= {})[k.locale] = url
}

/** 已放 loading 圖的 gameId（測試 / 診斷用） */
export const LOADING_BG_GAME_IDS: ReadonlySet<string> = new Set(Object.keys(BY_GAME))

/**
 * 這款有沒有註冊 loading 背景圖。**診斷 / 測試用，不參與任何行為判斷。**
 *
 * ⚠️ 2026-08-31 起**不再是 opt-in 訊號**。早期 `App.vue` 拿它當「GAME_READY 之前要不要
 * 走新版面」的提示，副作用是「圖檔存在與否」隱式決定了行為 —— 圖一補齊（現在 85 款全有）
 * 所有舊款就會被翻成新版面。現在**開關只看 Cocos 的 `GAME_READY.payload.hostLoading`**，
 * 放圖純粹是準備素材。
 */
export const hasLoadingBg = (gameId: string | null | undefined): boolean =>
  !!gameId && LOADING_BG_GAME_IDS.has(gameId)

/**
 * 取某款的 loading 背景圖 URL。
 *   - 有該語系 → 該語系；只有另一語系 → 退回那張（總比沒有好）；都沒有 → `undefined`（通用版）。
 *   - gameId 為空 / 未知 → `undefined`。
 */
export function resolveLoadingBg(
  gameId: string | null | undefined,
  locale: string,
): string | undefined {
  if (!gameId) return undefined
  const entry = BY_GAME[gameId]
  if (!entry) return undefined
  const want: Locale = locale === 'en' ? 'en' : 'zh'
  const other: Locale = want === 'en' ? 'zh' : 'en'
  return entry[want] ?? entry[other]
}

// ── splash 期間預取背景圖 ────────────────────────────────────────────────────

/** 目前掛在 `<head>` 的那張 preload link（只留最新一張；同 href 重呼叫 = no-op） */
let bgPreloadLink: HTMLLinkElement | null = null

/**
 * **在 logo splash 播放期間就把背景圖抓下來**（2026-08-28）。
 *
 * 🛑 為什麼需要：背景圖的 `<img>` 掛在 `LoadingScreen` 的 `v-else`（`hostLoading` 那半邊），
 *    而那半邊要等 `splashDone`（`LOGO_SPLASH_MS = 1500`）才渲染 —— 也就是
 *    **「切到新版面」那一刻背景圖才開始下載**。實測 stg rtt200：切換在 2.1s、
 *    圖 3.1s 才到 → 中間一整秒是沒有背景的空版面。
 *    預取把這 1s 挪到 splash 期間（那段時間網路本來就閒著）。
 *
 * 手法是 `<link rel="preload" as="image" fetchpriority="high">`：
 *   - **同源、不加 `crossorigin`** —— 必須與之後那個 `<img>` 的 CORS 模式一致，
 *     否則瀏覽器視為兩份不同的快取條目，會**重抓一次**（preload 白做且多一趟）。
 *   - `fetchpriority="high"`：同時在跑的 `logo_animation.webp`（894KB）已標
 *     `fetchpriority="low"`（見 `LoadingScreen.vue`），兩者一高一低才搶得贏。
 *   - 舊瀏覽器不支援 preload（`relList.supports` 為否）→ 退回 `new Image().src`，
 *     一樣進 image cache，只是沒有優先權旋鈕。
 *
 * 🛑 **舊款零風險**：沒放圖的 gameId `resolveLoadingBg` 回 `undefined` → 這裡直接 return，
 *    `<head>` 不會多出任何節點、不會多發任何請求。
 */
export function preloadLoadingBg(url: string | null | undefined): void {
  if (typeof document === 'undefined' || !url) return
  // 同一張已經預取過（例如 gameId 沒變、只是重跑 watch）→ 不重複插入
  if (bgPreloadLink?.getAttribute('href') === url) return
  // 換款 / 換語系：舊的那張已無意義，移除避免 <head> 累積節點
  bgPreloadLink?.remove()
  bgPreloadLink = null

  let supportsPreload: boolean
  try {
    supportsPreload = document.createElement('link').relList.supports('preload')
  } catch {
    supportsPreload = false
  }
  if (!supportsPreload) {
    new Image().src = url
    return
  }

  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  // property 形式（`link.fetchPriority`）在舊型別定義裡可能不存在 → 用 attribute，行為相同
  link.setAttribute('fetchpriority', 'high')
  link.href = url
  document.head.appendChild(link)
  bgPreloadLink = link
}
